<script setup lang="ts">
import { calculateValidity, stringToFaDateTime } from '@payjib/shared'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { acceptOfferAction } from '@/api/functions/offers/accept-offer-action'
import { deleteOfferAction } from '@/api/functions/offers/delete-offer-action'
import { rejectOfferAction } from '@/api/functions/offers/reject-offer-action'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'

const props = defineProps({
  offer: { type: Object, required: true },
})

const emit = defineEmits(['confirmOffer'])
const toast = useToast()
const router = useRouter()

const showDeleteModal = ref(false)
const showRejectModal = ref(false)
const showAcceptModal = ref(false)

const offerId = computed(() => props.offer?.uuid)

const cardType = computed(() => {
  return props?.offer?.type === 'BUY' ? true : false
})

const goToOfferDetails = () => {
  if (offerId.value) {
    router.push(`/offers/${offerId.value}`)
  }
}

// DELETE OFFER
const deleteMutation = useMutationApi({
  mutationFn: (id: string) => deleteOfferAction(id),
  onSuccess: (response) => {
    toast.success(response?.meta?.message || 'پیشنهاد با موفقیت حذف شد')
    emit('confirmOffer')
    showDeleteModal.value = false
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const handleDeleteOffer = async () => {
  if (!offerId.value) return
  deleteMutation.mutate(offerId.value)
}

// REJECT OFFER
const rejectMutation = useMutationApi({
  mutationFn: (id: string) => rejectOfferAction(id),
  onSuccess: (response) => {
    toast.success(response?.meta?.message || 'پیشنهاد با موفقیت رد شد')
    emit('confirmOffer')
    showRejectModal.value = false
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const handleRejectOffer = async () => {
  if (!offerId.value) return
  rejectMutation.mutate(offerId.value)
}

// ACCEPT OFFER
const acceptMutation = useMutationApi({
  mutationFn: (id: string) => acceptOfferAction(id),
  onSuccess: (response) => {
    toast.success(response?.meta?.message || 'پیشنهاد با موفقیت تایید شد')
    emit('confirmOffer')
    showAcceptModal.value = false
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const handleAcceptOffer = async () => {
  if (!offerId.value) return
  acceptMutation.mutate(offerId.value)
}
</script>

<template>
  <div v-if="offer"
    class="relative flex flex-col-reverse bg-white rounded-xl p-5 lg:p-8 text-black text-sm font-medium">

    <ConfirmModal v-model="showDeleteModal" title="حذف پیشنهاد" :message="'آیا از حذف این پیشنهاد اطمینان دارید؟'"
      confirm-text="بله، حذف شود" cancel-text="بازگشت" @confirm="handleDeleteOffer" @cancel="showDeleteModal = false" />

    <ConfirmModal v-model="showRejectModal" title="رد پیشنهاد" :message="'آیا از رد این پیشنهاد اطمینان دارید؟'"
      confirm-text="بله، رد شود" cancel-text="بازگشت" @confirm="handleRejectOffer" @cancel="showRejectModal = false" />

    <ConfirmModal v-model="showAcceptModal" title="تایید پیشنهاد" :message="'آیا از تایید این پیشنهاد اطمینان دارید؟'"
      confirm-text="بله، تایید شود" cancel-text="بازگشت" @confirm="handleAcceptOffer"
      @cancel="showAcceptModal = false" />

    <div class="flex items-center gap-2 relative lg:absolute bottom-0 lg:bottom-auto lg:top-8 lg:end-8 mt-6 lg:mt-0">
      <Button @click="goToOfferDetails" :isAdmin="true" class="w-full lg:w-[81px] h-10! py-0 text-[13px] font-normal">
        <span class="flex items-center justify-center gap-1.5">
          مشاهده
        </span>
      </Button>

      <Button v-if="offer?.status === 'PENDING'" @click="showAcceptModal = true" :isAdmin="true"
        class=" w-full lg:w-[81px] h-10! py-0 text-[13px] font-normal">
        <span class="flex items-center justify-center gap-1.5 text-nowrap">
          تایید
        </span>
      </Button>
      <Button v-if="offer?.status === 'PENDING'" @click="showRejectModal = true" :isAdmin="true" variant="outline"
        class=" w-full lg:w-[81px]  border-2  rounded-[10px]  h-10! py-0 text-[13px] font-normal ">
        <span class="text-nowrap flex items-center justify-center gap-2 ">
          رد
        </span>
      </Button>
      <Button v-if="offer?.status === 'PENDING'" @click="showDeleteModal = true" :isAdmin="true" variant="outline"
        class=" w-full lg:w-[81px]  border-2  rounded-[10px] h-10! py-0 text-[13px] font-normal">
        <span class="flex items-center justify-center gap-2 ">
          حذف
        </span>
      </Button>
    </div>

    <div class="flex flex-col gap-6">
      <!-- Header -->
      <div class="flex items-center justify-center lg:justify-between lg:pe-1">
        <div class="w-full lg:w-fit flex flex-col lg:flex-row items-center gap-2">

          <div
            class="w-full lg:w-auto flex items-center justify-center text-[13px] font-medium h-10 rounded-[10px] px-3"
            :class="offer?.status === 'EXPIRED' ? 'bg-[#fff8e7] text-[#ffba0e] ' :
              offer?.status === 'PENDING' ? 'bg-[#d5ddea] text-[#2d5396] ' :
                offer?.status === 'ACCEPTED' ? 'bg-[#e6fff0] text-[#2ed06e] ' :
                  offer?.status === 'REJECTED' ? 'bg-[#fde7ef] text-[#ed145b]' :
                    ' '">
            <span>پیشنهاد <span class="font-fa">{{ offer?.code }}</span></span>
            <span class="mx-1">|</span>
            <span class="ms-1">
              {{
                offer?.status === 'EXPIRED' ? 'منقضی شده' :
                  offer?.status === 'ACCEPTED' ? 'تایید شده' :
                    offer?.status === 'PENDING' ? 'بی‌پاسخ' :
                      offer?.status === 'REJECTED' ? 'رد شده' :
                        ''
              }}
            </span>
          </div>
          <div v-if="offer?.discount?.currentUser"
            class="w-full lg:w-fit flex items-center justify-center text-[13px] bg-[#f3d0f2] text-[#8c228a] font-medium h-10 rounded-[10px] px-6">
            دارای تخفیف
          </div>

        </div>
      </div>

      <!-- Body -->
      <div class="flex flex-col gap-2 text-sm text-black font-medium">
        <div class="flex items-center gap-1">

          <RouterLink v-if="offer?.owner?.id" :to="`/users/${offer.owner.uuid}`" class="text-[#2d5396] hover:underline">
            {{ offer?.owner?.fullName }}
          </RouterLink>
          <span v-else>{{ offer?.owner?.fullName }}</span>
          <span class="font-fa">( {{ offer?.owner?.id }})</span>
        </div>

        <div class="flex items-center gap-1 flex-wrap">
          <span>{{ cardType ? 'خرید' : 'فروش' }}</span>
          <span class="font-fa">{{ offer?.currencyAmount }}</span>
          <span>{{ offer?.currency?.titleFa }}</span>
        </div>
        <div class="flex items-center gap-1">
          <span>نرخ تبدیل پیشنهاد:</span>
          <span class="font-fa">{{ offer?.exchangeRate?.toLocaleString() }} تومان</span>
        </div>

        <div class="flex items-center gap-1">
          <span>نوع انتقال:</span>
          <span>
            {{
              offer?.transferMethods?.length > 0
                ? offer?.transferMethods.map((method: { label: string }) => method.label).join(', ')
                : 'تعیین نشده'
            }}
          </span>
        </div>

        <div class="flex items-center gap-1">
          <span>وضعیت درخواست:</span>
          <span>
            {{
              offer?.requisition?.status === 'PENDING' ? 'در حال انتظار' :
                offer?.requisition?.status === 'IN_TRANSACTION' ? 'در حال انجام' :
                  offer?.requisition?.status === 'EXPIRED' ? 'منقضی شده' :
                    offer?.requisition?.status === 'FINISHED' ? 'پایان یافته' :
                      '' }}
          </span>
        </div>

      </div>

      <!-- Footer -->
      <div
        class="flex flex-col lg:flex-row lg:items-center gap-6 mt-2 lg:mt-4 lg:gap-0 justify-between pe-1 text-sm text-[#1b325a] font-normal">

        <div v-if="offer.type === 'SELL' && offer.country.id"
          class="flex items-center gap-1 text-sm font-medium text-[##1b325a]">
          <img :src="offer.country.flagUrl" width="22" height="16" alt="flag" />
          {{ offer.country.title.fa }}
        </div>

        <div v-if="offer.type === 'BUY' && offer.countries" class="flex items-center gap-1">
          <div v-for="(country, index) in offer.countries" :key="`country${index}`"
            class="flex items-center gap-1 text-sm font-medium text-[##1b325a]">
            <img :src="country.flagUrl" width="22" height="16" alt="flag" />
            {{ country.title.fa }}
            <span v-if="index !== offer.countries.length - 1">,</span>
          </div>
        </div>

        <div class="flex flex-wrap items-center justify-between lg:justify-end text-sm font-medium gap-4">
          <div class="flex items-center">
            ثبت:<span dir="ltr" class="ms-1 font-fa">{{ stringToFaDateTime(offer?.createdAt) }}</span>
          </div>
          <div class="flex items-center">اعتبار:
            <span class="font-fa mx-1">
              {{ calculateValidity(offer?.expiresAt) }}
            </span>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>
